import {
  AgenciesByNetworkParams,
  AgenciesByNetworkResponse,
  AllNetworksResponse,
  answerPartnershipRequest,
  CountriesResponse,
  DistributorFilterResponse,
  DistributorParams,
  findAgenciesByNetwork,
  findAllRecentAgenciesGroupedByNetwork,
  findDistributorsToShareCircuitsWith,
  findReceivedPartnershipRequests,
  getAllNetworks,
  getCircuitsForAgencies,
  getDistributors,
  getDistributorsCircuits,
  getDistributorsCountries,
  getDistributorsNetworks,
  getPartnerAgencyInfo,
  NetworkParams,
  removeDistributor,
  ShareCircuitsParams,
  shareCircuitsWithAgencies,
} from '@/api/distributor.api';
import { PartnershipRequestsParam } from '@/api/receiver.api';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const useGetMyDistributors = (params: DistributorParams) =>
  useQuery({
    queryKey: queryKeys.distributors.list(params),
    queryFn: () => getDistributors(params),
    placeholderData: keepPreviousData,
  });

export const useGetDistributorsNetworks = <TData = DistributorFilterResponse>(
  select?: (data: DistributorFilterResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.distributors.networks,
    queryFn: () => getDistributorsNetworks(),
    select,
  });

export const useGetDistributorsCountries = <TData = CountriesResponse>(
  select?: (data: CountriesResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.distributors.countries,
    queryFn: () => getDistributorsCountries(),
    select,
  });

export const useGetDistributorsCircuits = <TData = DistributorFilterResponse>(
  select?: (data: DistributorFilterResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.distributors.circuits,
    queryFn: () => getDistributorsCircuits(),
    select,
  });

export const useGetAllNetworks = <TData = AllNetworksResponse>(
  params: NetworkParams,
  select?: (data: AllNetworksResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.distributors.networksList(params),
    queryFn: () => getAllNetworks(params),
    select,
  });

export const useGetCircuitsForAgencies = (
  agencies: string[],
  networks: string[] = [],
) =>
  useQuery({
    queryKey: queryKeys.distributors.circuitsForAgencies(agencies, networks),
    queryFn: () => getCircuitsForAgencies(agencies, networks),
  });

export const useShareCircuitsWithAgencies = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: ShareCircuitsParams) =>
      shareCircuitsWithAgencies(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distributors.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributors.circuits,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributors.agencies,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.circuits.all });
    },
  });
};

export const useRemoveDistributor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (distributorId: string) => removeDistributor(distributorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distributors.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributors.circuits,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributors.agencies,
      });
    },
  });
};

export const useFindDistributorsToShareCircuitsWith = ({
  circuitIds,
}: {
  circuitIds: string[];
}) =>
  useQuery({
    queryKey: queryKeys.distributors.agenciesByCircuit(circuitIds),
    queryFn: () => findDistributorsToShareCircuitsWith(circuitIds),
  });

export const useFindAllRecentAgenciesGroupedByNetwork = (
  params: NetworkParams,
) =>
  useQuery({
    queryKey: queryKeys.distributors.agenciesRecent(params),
    queryFn: () => findAllRecentAgenciesGroupedByNetwork(params),
  });

export const useFindAgenciesByNetwork = <TData = AgenciesByNetworkResponse>(
  networkId: string,
  params: AgenciesByNetworkParams,
  select?: (data: AgenciesByNetworkResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.distributors.agenciesByNetwork(networkId, params),
    queryFn: () => findAgenciesByNetwork(networkId, params),
    placeholderData: keepPreviousData,
    select,
  });

export const useFindReceivedPartnershipRequests = (
  params: PartnershipRequestsParam,
) =>
  useQuery({
    queryKey: queryKeys.distributors.partnershipRequests(params),
    queryFn: () => findReceivedPartnershipRequests(params),
    placeholderData: keepPreviousData,
  });

export const useAnswerPartnershipRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      distributorId,
      isAccepted,
    }: {
      distributorId: string;
      isAccepted: boolean;
    }) => answerPartnershipRequest({ distributorId, isAccepted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.distributors.all });
    },
  });
};

export const useGetPartnerAgencyInfo = (agencyId: string) =>
  useQuery({
    queryKey: queryKeys.distributors.partnerAgencyInfo(agencyId),
    queryFn: () => getPartnerAgencyInfo(agencyId),
  });
